import { createClient, AuthError, SupabaseClient, processLock } from '@supabase/supabase-js';
import dotenv from 'dotenv';
import type { Database } from './database.types';

// Load environment variables in Node.js environment
if (typeof process !== 'undefined') {
  dotenv.config();
}

// Get environment variables from either Vite or Node.js environment
export const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
export const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

/**
 * OAuth config result type
 */
export interface OAuthConfigResult {
  sessionStatus: {
    hasSession: boolean;
    expiresAt: number | null;
  };
  hasSession: boolean;
  expiresAt: number | null;
  oauthProviders: string[];
  redirectUrls: string[];
  urlConfigValid: boolean;
  currentUrl: string;
  error: AuthError | null;
}

/**
 * Function-based client interface with role separation.
 * This prevents "Multiple GoTrueClient instances detected" warnings
 * by maintaining a single client instance with explicit role-based functions.
 */
interface SupabaseInterface {
  // Base client (used internally)
  client: SupabaseClient<Database>;
  
  // Direct client methods for backward compatibility
  from: (table: string) => any;
  rpc: (fn: string, params?: any) => any;
  storage: any;
  channel: (name: string) => any;
  removeChannel: (channel: any) => any;
  
  // Public operations (no auth required)
  public: {
    fetchAddresses: (query: string) => Promise<any>;
    fetchAddressById: (objectId: number) => Promise<any>;
    fetchAllRows: (tableName: keyof Database['public']['Tables']) => Promise<any>;
    fetchStationById: (objectId: number) => Promise<any>; // Fetches from private schema
  };
  
  // Auth operations
  auth: {
    signInWithEmail: (email: string, password: string) => Promise<any>;
    signUpWithEmail: (email: string, password: string, metadata?: { [key: string]: any }) => Promise<any>;
    signInWithGoogle: (stateData?: { redirectPath?: string; valuationId?: string }) => Promise<any>;
    signOut: () => Promise<{ error: AuthError | null }>;
    getSession: () => Promise<any>;
    getUser: () => Promise<any>;
    checkOAuthConfig: () => Promise<OAuthConfigResult>;
    updateUser: (attributes: { email?: string; password?: string; data?: object }) => Promise<any>;
    resetPasswordForEmail: (email: string, options?: { redirectTo?: string }) => Promise<any>;

    // Additional auth methods for backward compatibility
    signInWithPassword: (credentials: { email: string; password: string }) => Promise<any>;
    signUp: (credentials: { email: string; password: string; options?: any }) => Promise<any>;
    onAuthStateChange: (callback: (event: any, session: any) => void) => any;
    refreshSession: () => Promise<any>;
  };
  
  // Secured operations (require auth)
  secured: {
    getUserProfile: () => Promise<any>;
    updateUserProfile: (data: any) => Promise<any>;
  };
}

// Global singleton management
declare global {
  interface Window {
    __gasmanapp_supabase_instance__?: SupabaseInterface;
    __gasmanapp_client_count__?: number;
    __gasmanapp_module_loads__?: number;
  }
}

// Singleton instance
let instance: SupabaseInterface | null = null;

// Check if module is being executed multiple times
if (typeof window !== 'undefined') {
  if (window.__gasmanapp_module_loads__) {
    console.error('supabase.ts module is being loaded multiple times!');
    console.error('Previous loads:', window.__gasmanapp_module_loads__);
    console.trace();
  }
  window.__gasmanapp_module_loads__ = (window.__gasmanapp_module_loads__ || 0) + 1;
  console.log(`supabase.ts module loaded ${window.__gasmanapp_module_loads__} times`);
}

/**
 * Creates a Supabase client with functional separation for different auth contexts.
 * Uses a true singleton pattern to prevent multiple client instances.
 */
function getSupabaseInstance(): SupabaseInterface {
  // Check if we already have an instance
  if (instance) {
    console.log('Returning existing Supabase instance');
    return instance;
  }

  // In browser environment, check for existing global instance
  if (typeof window !== 'undefined') {
    if (window.__gasmanapp_supabase_instance__) {
      console.log('Reusing existing Supabase instance from global window');
      instance = window.__gasmanapp_supabase_instance__;
      return instance;
    }
    
    // Track client creation
    window.__gasmanapp_client_count__ = (window.__gasmanapp_client_count__ || 0) + 1;
    console.log(`Creating Supabase client instance #${window.__gasmanapp_client_count__}`);
    
    if (window.__gasmanapp_client_count__ > 1) {
      console.error('MULTIPLE SUPABASE CLIENTS DETECTED!');
      console.error('This will cause auth issues. Stack trace:');
      console.trace();
    }
  } else {
    console.log('Creating Supabase client instance in Node.js environment');
  }

  // Create the actual client.
  // `lock: processLock` replaces the default navigator.locks-based lock
  // with a per-tab in-process lock. The default cross-tab lock can hang
  // indefinitely if a sibling tab crashes mid-refresh, which freezes
  // every Supabase call in the surviving tab (getSession, queries, auth
  // calls). processLock is per-tab and cannot get stuck across tabs.
  const client = createClient<Database>(supabaseUrl, supabaseAnonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      flowType: 'implicit',
      storageKey: 'gasmanapp-main-auth-storage',
      debug: false,
      storage: typeof window !== 'undefined' ? window.localStorage : undefined,
      lock: processLock,
    }
  });

  // Create the interface
  instance = {
    client,
    
    // Direct client methods for backward compatibility
    from: (table: string) => client.from(table as any),
    rpc: (fn: string, params?: any) => (client as any).rpc(fn, params),
    storage: client.storage,
    channel: (name: string) => client.channel(name),
    removeChannel: (channel: any) => client.removeChannel(channel),
    
    // Public operations (no auth required)
    public: {
      fetchAddresses: async (query: string) => {
        try {
          const result = await client
            .from('Riverside_County_Station_List_Public')
            .select('*')
            .ilike('Facility_Address', `%${query}%`)
            .limit(10);
          return result;
        } catch (error) {
          console.error('Error fetching addresses:', error);
          return { data: [], error };
        }
      },
      
      fetchAddressById: async (objectId: number) => {
        try {
          const result = await client
            .from('Riverside_County_Station_List_Public')
            .select('*')
            .eq('OBJECTID', objectId)
            .single();
          return result;
        } catch (error) {
          console.error('Error fetching address by ID:', error);
          return { data: null, error };
        }
      },
      
      fetchAllRows: async (tableName: keyof Database['public']['Tables']) => {
        try {
          const result = await client
            .from(tableName)
            .select('*');
          return result;
        } catch (error) {
          console.error(`Error fetching rows from ${tableName}:`, error);
          return { data: [], error };
        }
      },

      fetchStationById: async (objectId: number) => {
        try {
          console.log('Fetching station from private schema using RPC, OBJECTID:', objectId);

          // Use RPC function with SECURITY DEFINER to access private schema
          // This function is defined in migration 20251119000000_create_private_schema_access.sql
          // Returns JSONB object directly (not an array)
          const { data, error } = await (client as any)
            .rpc('get_station_by_objectid', { objectid_param: objectId });

          console.log('Station fetch result:', { data, error });

          if (error) {
            console.error('RPC error:', error);
            return { data: null, error };
          }

          // RPC now returns JSONB directly (not an array)
          return { data: data || null, error: null };
        } catch (error) {
          console.error('Error fetching station by ID from private schema:', error);
          return { data: null, error };
        }
      }
    },
    
    // Auth operations
    auth: {
      signInWithEmail: async (email: string, password: string) => {
        return client.auth.signInWithPassword({ email, password });
      },
      
      signUpWithEmail: async (email: string, password: string, metadata?: { [key: string]: any }) => {
        return client.auth.signUp({
          email,
          password,
          options: {
            data: metadata,
            emailRedirectTo: `${window.location.origin}/auth/callback`
          }
        });
      },
      
      signInWithGoogle: async (stateData?: { redirectPath?: string; valuationId?: string }) => {
        console.log('Starting Google OAuth flow...');
        console.log('Current origin:', window.location.origin);
        console.log('State data to preserve:', stateData);
        
        // Store state data in sessionStorage as backup since OAuth state can be unreliable
        if (stateData) {
          sessionStorage.setItem('oauth_state_backup', JSON.stringify(stateData));
          sessionStorage.setItem('oauth_start_time', Date.now().toString());
          console.log('Stored OAuth state backup in sessionStorage');
        }
        
        // Use only the redirectTo parameter to avoid state conflicts
        const options = {
          redirectTo: `${window.location.origin}/auth/callback`
        };
        
        console.log('OAuth redirect URL:', options.redirectTo);
        
        return client.auth.signInWithOAuth({
          provider: 'google',
          options
        });
      },
      
      signOut: async () => {
        return client.auth.signOut();
      },
      
      getSession: async () => {
        return client.auth.getSession();
      },
      
      getUser: async () => {
        return client.auth.getUser();
      },
      
      updateUser: async (attributes: { email?: string; password?: string; data?: object }) => {
        return client.auth.updateUser(attributes);
      },

      resetPasswordForEmail: async (email: string, options?: { redirectTo?: string }) => {
        return client.auth.resetPasswordForEmail(email, options);
      },

      // Additional auth methods for backward compatibility
      signInWithPassword: async (credentials: { email: string; password: string }) => {
        return client.auth.signInWithPassword(credentials);
      },
      
      signUp: async (credentials: { email: string; password: string; options?: any }) => {
        return client.auth.signUp(credentials);
      },
      
      onAuthStateChange: (callback: (event: any, session: any) => void) => {
        return client.auth.onAuthStateChange(callback);
      },
      
      refreshSession: async () => {
        return client.auth.refreshSession();
      },
      
      checkOAuthConfig: async (): Promise<OAuthConfigResult> => {
        try {
          const sessionResponse = await client.auth.getSession();
          const hasSession = !!sessionResponse.data.session;
          const expiresAt = sessionResponse.data.session?.expires_at || null;
          
          let oauthProviders: string[] = [];
          let redirectUrls: string[] = [];
          
          // Fetch settings with a direct API call since getSettings isn't available
          try {
            const response = await fetch(`${supabaseUrl}/auth/v1/settings`, {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                'apikey': supabaseAnonKey
              }
            });
            
            const settings = await response.json();
            oauthProviders = settings?.external?.providers || [];
            redirectUrls = settings?.external?.redirect_urls || [];
          } catch (err) {
            console.error('Error fetching OAuth settings:', err);
          }
          
          const currentUrl = typeof window !== 'undefined' ? window.location.origin : '';
          const urlConfigValid = redirectUrls.some((url: string) => 
            currentUrl.includes(url) || url.includes(currentUrl)
          );
          
          return {
            sessionStatus: {
              hasSession,
              expiresAt
            },
            hasSession,
            expiresAt,
            oauthProviders,
            redirectUrls,
            urlConfigValid,
            currentUrl,
            error: null
          };
        } catch (err) {
          console.error('Error checking OAuth config:', err);
          return {
            sessionStatus: { hasSession: false, expiresAt: null },
            hasSession: false,
            expiresAt: null,
            oauthProviders: [],
            redirectUrls: [],
            urlConfigValid: false,
            currentUrl: typeof window !== 'undefined' ? window.location.origin : '',
            error: err instanceof AuthError ? err : null
          };
        }
      }
    },
    
    // Secured operations (require auth)
    secured: {
      getUserProfile: async () => {
        const { data: { user } } = await client.auth.getUser();
        if (!user) throw new Error('Not authenticated');
        
        return client
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();
      },
      
      updateUserProfile: async (data: any) => {
        const { data: { user } } = await client.auth.getUser();
        if (!user) throw new Error('Not authenticated');
        
        return client
          .from('profiles')
          .update(data)
          .eq('id', user.id);
      }
    }
  };

  // Store in global window for hot reload protection
  if (typeof window !== 'undefined') {
    window.__gasmanapp_supabase_instance__ = instance;
    console.log('Stored Supabase instance in global window');
  }

  return instance;
}

// Initialize singleton immediately and export it
const _supabaseInstance = getSupabaseInstance();

// Export the singleton instance directly (no proxy needed)
export const supabase = _supabaseInstance;

// Export OAuthConfigResult type and functions for backward compatibility
export { AuthError };

